import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  Moon, Home, Calendar, Flower2, Star, Sparkles, 
  MessageCircle, Settings, Menu, LogOut, X, CalendarCheck, BarChart3, BookOpen, Heart, Users, Orbit, Trophy, Hash, Shield, Activity, HeartPulse
} from "lucide-react";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import ForumNotificationsBell from "@/components/ForumNotificationsBell";

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  const navItems = [
    { path: "/dashboard", icon: Home, label: t("nav.dashboard") },
    { path: "/calendar", icon: Calendar, label: t("nav.calendar") },
    { path: "/journal", icon: BookOpen, label: t("nav.journal") },
    { path: "/daily-horoscope", icon: Star, label: t("nav.horoscope") },
    { path: "/predictions", icon: CalendarCheck, label: t("nav.predictions") },
    { path: "/analytics", icon: BarChart3, label: t("nav.analytics") },
    { path: "/symptom-trends", icon: Activity, label: "Symptom Trends" },
    { path: "/health-data", icon: HeartPulse, label: "Health Data" },
    { path: "/moon", icon: Moon, label: t("nav.moonCycles") },
    { path: "/womb-healing", icon: Heart, label: t("nav.wombHealing") },
    { path: "/remedies", icon: Flower2, label: t("nav.remedies") },
    { path: "/astrology", icon: Sparkles, label: t("nav.astrology") },
    { path: "/transits", icon: Orbit, label: t("nav.transits") },
    { path: "/synastry", icon: Heart, label: t("nav.synastry") },
    { path: "/rituals", icon: Sparkles, label: t("nav.rituals") },
    { path: "/numerology", icon: Hash, label: t("nav.numerology") },
    { path: "/achievements", icon: Trophy, label: t("nav.achievements") },
    { path: "/ai-tips", icon: MessageCircle, label: t("nav.aiTips") },
    { path: "/forum", icon: Users, label: t("nav.forum") },
    { path: "/settings", icon: Settings, label: t("nav.settings") }
  ];

  // Add admin nav item for admin users
  const adminEmails = ["montricewinters.mw@gmail.com", "daizyinfused@gmail.com"];
  if (user?.email && adminEmails.includes(user.email)) {
    navItems.push({ path: "/admin", icon: Shield, label: t("nav.admin") || "Admin" });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const NavContent = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 flex-shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2" onClick={onNavigate}>
          <img src="/logo.png" alt="Sacred Celestial Flow" className="w-10 h-10 object-contain" />
          <span className="text-lg font-bold gradient-text">Sacred Celestial Flow</span>
        </Link>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 px-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-mystic-600 scrollbar-track-transparent">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                isActive
                  ? "bg-mystic-700/50 text-white border border-mystic-300/30"
                  : "text-mystic-200/70 hover:text-white hover:bg-mystic-800/50"
              }`}
            >
              <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-mystic-300" : ""}`} />
              <span className="font-medium text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User & Logout */}
      <div className="p-4 border-t border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3 px-4 py-2 mb-2">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-mystic-700 flex items-center justify-center">
              <span className="text-mystic-300 text-sm">{user?.name?.charAt(0)}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-mystic-400 text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-mystic-200/70 hover:text-red-400 hover:bg-red-500/10"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-3" />
          {t("nav.signOut")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative z-10">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 h-full w-64 glass-heavy border-r border-white/5">
        <NavContent onNavigate={() => {}} />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-heavy border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/logo.png" alt="Sacred Celestial Flow" className="w-8 h-8 object-contain" />
            <span className="text-lg font-bold gradient-text">Sacred Celestial Flow</span>
          </Link>

          <div className="flex items-center gap-1">
            <ForumNotificationsBell />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="glass-heavy border-white/10 p-0 w-72">
                <NavContent onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="container mx-auto px-4 py-6 lg:py-8 pt-20 lg:pt-8 max-w-6xl">
          {/* Desktop notification bell - floating top-right */}
          <div className="hidden lg:flex justify-end -mt-2 mb-2">
            <ForumNotificationsBell />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
